import React, { useState, useCallback } from 'react';
import * as RadioGroup from '@radix-ui/react-radio-group';
import { Download } from 'lucide-react';
import AppModal from '../components/Modal/AppModal';

type ExportFormat = 'pdf' | 'csv' | 'excel' | 'json';

interface ExportDialogProps {
  open: boolean;
  format: ExportFormat;
  loading: boolean;
  language: string;
  t: any;
  onClose: () => void;
  onFormatChange: (format: ExportFormat) => void;
  onExport: () => void;
}

const FORMAT_LABELS: Record<ExportFormat, { title: string; subtitle: (t: any) => string }> = {
  pdf:   { title: 'PDF Report',    subtitle: (t) => t.exportDialog.pdfDescription },
  csv:   { title: 'CSV Spreadsheet', subtitle: (t) => t.exportDialog.csvDescription },
  excel: { title: 'Excel Workbook', subtitle: (t) => t.exportDialog.excelDescription },
  json:  { title: 'JSON Data',     subtitle: (t) => t.exportDialog.jsonDescription },
};

const ExportDialogComponent = React.memo<ExportDialogProps>(({
  open,
  format,
  loading,
  language: _language,
  t,
  onClose,
  onFormatChange,
  onExport,
}) => (
  <AppModal
    open={open}
    onClose={onClose}
    title={t.exportDialog?.exportData || 'Export'}
    maxWidth="sm"
    icon={<Download size={20} />}
    actions={{
      secondary: {
        label: t.exportDialog.cancel,
        onClick: onClose,
        disabled: loading,
        variant: 'text',
        color: 'secondary',
      },
      primary: {
        label: loading ? t.exportDialog.exporting : t.exportDialog.download,
        onClick: onExport,
        disabled: loading,
        variant: 'contained',
        color: 'primary',
        startIcon: loading
          ? <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          : <Download size={16} />,
      },
    }}
  >
    <p className="mb-4 text-sm text-[var(--text-secondary)]">
      {t.exportDialog.downloadDescription}
    </p>

    <p className="mb-3 text-sm font-semibold text-[var(--text-color)]">
      {t.exportDialog.selectExportFormat}
    </p>

    <RadioGroup.Root
      value={format}
      onValueChange={(v) => onFormatChange(v as ExportFormat)}
      className="space-y-2"
    >
      {(['pdf', 'csv', 'excel', 'json'] as const).map((fmt) => (
        <RadioGroup.Item
          key={fmt}
          value={fmt}
          id={`fmt-${fmt}`}
          className="hidden"
        />
      ))}
      {(['pdf', 'csv', 'excel', 'json'] as const).map((fmt) => (
        <label
          key={fmt}
          htmlFor={`fmt-${fmt}`}
          className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
            format === fmt
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-white/10 text-[var(--text-color)] hover:border-white/20'
          }`}
        >
          <div
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              format === fmt ? 'border-primary' : 'border-white/30'
            }`}
          >
            {format === fmt && <div className="w-2 h-2 rounded-full bg-primary" />}
          </div>
          <div>
            <div className="text-sm font-medium">{FORMAT_LABELS[fmt].title}</div>
            <div className="text-xs text-[var(--text-secondary)]">
              {FORMAT_LABELS[fmt].subtitle(t)}
            </div>
          </div>
        </label>
      ))}
    </RadioGroup.Root>
  </AppModal>
));

ExportDialogComponent.displayName = 'ExportDialogComponent';

interface UseExportDialogParams {
  language: string;
  t: any;
  onExport: (format: ExportFormat) => Promise<void>;
}

export const useExportDialog = ({ language, t, onExport }: UseExportDialogParams) => {
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>('pdf');
  const [loading, setLoading] = useState(false);

  const handleExport = useCallback(async () => {
    setLoading(true);
    try {
      await onExport(exportFormat);
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setLoading(false);
    }
  }, [exportFormat, onExport]);

  return {
    exportDialogOpen,
    setExportDialogOpen,
    exportFormat,
    setExportFormat,
    loading,
    handleExport,
    ExportDialog: (props: Omit<ExportDialogProps, 'format' | 'loading' | 'language' | 't' | 'onFormatChange' | 'onExport'>) => (
      <ExportDialogComponent
        {...props}
        format={exportFormat}
        loading={loading}
        language={language}
        t={t}
        onFormatChange={setExportFormat}
        onExport={handleExport}
      />
    ),
  };
};
